import type { RentalUnit, DurationPreset } from "@/types/rental"
import { HttpTypes } from "@medusajs/types"

export interface RentalItemMetadata {
  isRental: boolean
  startDate: string
  endDate: string
  rentalPeriod: number
  rentalUnit: RentalUnit
  totalRentalCost: number
  totalDeposit: number
  pricePerUnit: number
}

/**
 * Check if a cart contains any rental items
 */
export function hasRentalItems(cart: HttpTypes.StoreCart | null): boolean {
  if (!cart?.items) return false
  return cart.items.some((item) => {
    const metadata = item.metadata as RentalItemMetadata | undefined
    return metadata?.isRental === true
  })
}

/**
 * Должна ли корзина оставить карту.
 *
 * Запрет наличных у аренды — это запрет не на аренду, а на **залог**: его
 * держат на карте, и наличными его обеспечить нечем. Так и решает бэк
 * (`api/middlewares.ts`, CHK-005): манулальная оплата блокируется, только когда
 * `deposit.total > 0`, и аренда товара без залога явным образом разрешена.
 *
 * Витрина же до 2026-11-03 прятала наличные у ЛЮБОЙ аренды, то есть была строже
 * собственного бэка: самовывоз книги без залога нельзя было оплатить на месте,
 * хотя магазин это позволяет.
 */
export function cartRequiresCard(cart: HttpTypes.StoreCart | null): boolean {
  if (!cart?.items) return false
  return cart.items.some((item) => {
    const metadata = item.metadata as RentalItemMetadata | undefined
    if (metadata?.isRental !== true) return false
    const deposit = Number((metadata as Record<string, any>)?.depositAmount ?? 0)
    return Number.isFinite(deposit) && deposit > 0
  })
}

/**
 * Get all rental items from cart
 */
export function getRentalItems(cart: HttpTypes.StoreCart | null): HttpTypes.StoreCartLineItem[] {
  if (!cart?.items) return []
  return cart.items.filter((item) => {
    const metadata = item.metadata as RentalItemMetadata | undefined
    return metadata?.isRental === true
  })
}

/**
 * Get all non-rental (purchase) items from cart
 */
export function getPurchaseItems(cart: HttpTypes.StoreCart | null): HttpTypes.StoreCartLineItem[] {
  if (!cart?.items) return []
  return cart.items.filter((item) => {
    const metadata = item.metadata as RentalItemMetadata | undefined
    return metadata?.isRental !== true
  })
}

/**
 * Check if cart is rental-only (no purchase items)
 */
export function isRentalOnlyCart(cart: HttpTypes.StoreCart | null): boolean {
  if (!cart?.items?.length) return false
  return cart.items.every((item) => {
    const metadata = item.metadata as RentalItemMetadata | undefined
    return metadata?.isRental === true
  })
}


/**
 * Get return shipping method name from cart metadata
 */
export function getReturnShippingMethodName(cart: HttpTypes.StoreCart | null): string | null {
  if (!cart?.metadata) return null
  return (cart.metadata as any).returnShippingMethodName || null
}

/**
 * Сколько из уплаченного — это обратная доставка.
 *
 * Пишет это бэк при выборе способа доставки: обратная нога входит в его цену,
 * и её доля записывается в метаданные корзины. На заказе работает тот же ключ —
 * `completeCartWorkflow` переносит `cart.metadata` в `order.metadata`, поэтому
 * страница «спасибо за заказ» знает сумму с первой отрисовки.
 */
export function getReturnShippingCost(cart: HttpTypes.StoreCart | null): number {
  if (!cart?.metadata) return 0
  return (cart.metadata as any).returnShippingCost || 0
}

/**
 * Какую ногу возврата корзина уже несёт: `"store"`, `"zasilkovna"` или ничего.
 *
 * Пишет её бэк, когда покупатель выбирает способ доставки: пара (отправка,
 * возврат) — это одна опция, и её код говорит, что именно куплено
 * (`lib/return-leg-cart.ts` в админке). Тот же ключ раньше заполняла сервисная
 * позиция, поэтому старые корзины читаются отсюда же.
 */
export function getReturnShippingType(cart: HttpTypes.StoreCart | null): string | null {
  const value = (cart?.metadata as Record<string, unknown> | null)?.returnShippingType
  return typeof value === "string" && value ? value : null
}

/**
 * Does the customer bring the rental back himself (a free return)?
 *
 * The answer is `returnType` on the return line — `"store" | "zasilkovna"`, written
 * by `POST /store/cart/:id/return-shipping` next to the line it creates. This used
 * to read `returnShippingMethodName.includes("store")` on the product title, and
 * every title in the shop is translated — «Возврат в магазин», «Vrácení do
 * prodejny» — so the free return recognised itself in English only, and the review
 * step called it paid in every other language.
 *
 * The title check stays as the last resort: an old cart, made before the line
 * carried `returnType`, still has the name in its metadata.
 */
export function isReturnToStore(cart: HttpTypes.StoreCart | null): boolean {
  const line = (cart?.items ?? []).find(
    (item) => (item.metadata as Record<string, unknown> | null)?.isReturnShipping === true
  )
  const type =
    ((line?.metadata as Record<string, unknown> | null)?.returnType as string | undefined) ??
    // Способ доставки знает ногу раньше всех: она куплена в его цене.
    (getReturnShippingType(cart) as string | undefined)
  if (type) return type === "store"

  const name = (cart?.metadata as Record<string, unknown> | null)?.returnShippingMethodName
  return typeof name === 'string' && name.toLowerCase().includes("store")
}

/**
 * Add rental periods to a date
 */
export function addRentalPeriod(
  date: Date,
  periods: number,
  unit: RentalUnit
): Date {
  const newDate = new Date(date)

  switch (unit) {
    case 'day':
      newDate.setDate(newDate.getDate() + periods)
      break
    case 'week':
      newDate.setDate(newDate.getDate() + (periods * 7))
      break
    case 'month':
      newDate.setMonth(newDate.getMonth() + periods)
      break
  }

  return newDate
}

/**
 * Get allowed date range for start date selection
 * Constraint: tomorrow to tomorrow + 7 days (rental cannot start today)
 */
export function getAllowedDateRange(): { minDate: Date; maxDate: Date } {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Rental can only start from tomorrow
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  const maxDate = new Date(tomorrow)
  maxDate.setDate(maxDate.getDate() + 7)

  return { minDate: tomorrow, maxDate }
}

/**
 * Check if a date is blocked (before tomorrow or after tomorrow+7)
 */
export function isDateBlocked(date: Date): boolean {
  const { minDate, maxDate } = getAllowedDateRange()
  const checkDate = new Date(date)
  checkDate.setHours(0, 0, 0, 0)
  return checkDate < minDate || checkDate > maxDate
}

/**
 * Check if a date is within allowed range
 */
export function isDateAllowed(date: Date): boolean {
  return !isDateBlocked(date)
}

/**
 * Get preset duration options based on rental unit
 */
export function getDurationPresets(
  unit: RentalUnit,
  minDuration: number = 1,
  maxDuration: number | null = null
): DurationPreset[] {
  let presets: DurationPreset[]

  switch (unit) {
    case 'day':
      presets = [
        { label: '1', value: 1, unit: 'day' },
        { label: '3', value: 3, unit: 'day' },
        { label: '7', value: 7, unit: 'day' },
        { label: '14', value: 14, unit: 'day' },
      ]
      break
    case 'week':
      presets = [
        { label: '1', value: 1, unit: 'week' },
        { label: '2', value: 2, unit: 'week' },
        { label: '4', value: 4, unit: 'week' },
      ]
      break
    case 'month':
      presets = [
        { label: '1', value: 1, unit: 'month' },
        { label: '2', value: 2, unit: 'month' },
        { label: '3', value: 3, unit: 'month' },
        { label: '6', value: 6, unit: 'month' },
      ]
      break
  }

  // Filter by min/max constraints
  return presets.filter(p => {
    if (p.value < minDuration) return false
    if (maxDuration !== null && p.value > maxDuration) return false
    return true
  })
}

/**
 * Get unit label for display
 */
export function getUnitLabel(
  unit: RentalUnit | undefined | null,
  count: number,
  locale: string = 'ru'
): string {
  // Handle undefined/null unit
  if (!unit) {
    return ''
  }

  const labels: Record<string, Record<RentalUnit, [string, string, string]>> = {
    ru: {
      day: ['день', 'дня', 'дней'],
      week: ['неделя', 'недели', 'недель'],
      month: ['месяц', 'месяца', 'месяцев'],
    },
    en: {
      day: ['day', 'days', 'days'],
      week: ['week', 'weeks', 'weeks'],
      month: ['month', 'months', 'months'],
    },
    cs: {
      day: ['den', 'dny', 'dní'],
      week: ['týden', 'týdny', 'týdnů'],
      month: ['měsíc', 'měsíce', 'měsíců'],
    },
    uk: {
      day: ['день', 'дні', 'днів'],
      week: ['тиждень', 'тижні', 'тижнів'],
      month: ['місяць', 'місяці', 'місяців'],
    },
  }

  const unitLabels = labels[locale] || labels['en']
  const forms = unitLabels[unit]

  // Handle invalid unit
  if (!forms) {
    return ''
  }

  // Slavic plural rules (Russian, Czech, Ukrainian)
  if (locale === 'ru' || locale === 'uk' || locale === 'cs') {
    const n = Math.abs(count) % 100
    const n1 = n % 10
    if (n > 10 && n < 20) return forms[2]
    if (n1 > 1 && n1 < 5) return forms[1]
    if (n1 === 1) return forms[0]
    return forms[2]
  }

  // English plural rules
  return count === 1 ? forms[0] : forms[1]
}

/**
 * Format rental period for display
 */
export function formatRentalPeriod(
  duration: number,
  unit: RentalUnit,
  locale: string = 'ru'
): string {
  const unitLabel = getUnitLabel(unit, duration, locale)
  return `${duration} ${unitLabel}`
}

/**
 * What a rental costs: the rent, the deposit, and the sum actually charged.
 *
 * `total` used to be rent + deposit, which is what the checkout charged too. A
 * deposit is not a purchase: it is held on the card and given back, so it is no
 * longer part of the cart's price (the hold is taken separately by the rental
 * module). Keeping it in `total` overstated every price on the product page and,
 * worse, invited a promo code to discount money we intend to return.
 */
export function calculateRentalTotal(
  rentalPrice: number,
  depositAmount: number,
  duration: number,
  quantity: number
): { rental: number; deposit: number; total: number } {
  const rental = rentalPrice * duration * quantity
  const deposit = depositAmount * quantity
  return {
    rental,
    deposit,
    total: rental,
  }
}

/**
 * Format date for display
 */
export function formatDate(date: Date, locale: string = 'ru'): string {
  const localeMap: Record<string, string> = {
    ru: 'ru-RU',
    en: 'en-US',
    cs: 'cs-CZ',
    uk: 'uk-UA',
  }

  return date.toLocaleDateString(localeMap[locale] || 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/**
 * Format short date for calendar display
 */
export function formatShortDate(date: Date, locale: string = 'ru'): string {
  const localeMap: Record<string, string> = {
    ru: 'ru-RU',
    en: 'en-US',
    cs: 'cs-CZ',
    uk: 'uk-UA',
  }

  return date.toLocaleDateString(localeMap[locale] || 'en-US', {
    day: 'numeric',
    month: 'short',
  })
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  )
}

/**
 * Get array of dates between start and end (inclusive)
 */
export function getDatesBetween(start: Date, end: Date): Date[] {
  const dates: Date[] = []
  const current = new Date(start)
  current.setHours(0, 0, 0, 0)
  const endDate = new Date(end)
  endDate.setHours(0, 0, 0, 0)

  while (current <= endDate) {
    dates.push(new Date(current))
    current.setDate(current.getDate() + 1)
  }

  return dates
}
